import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  Paper,
  Stack,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  OutlinedInput,
  Drawer,
  Select,
  MenuItem
} from "@mui/material";
import GlobalSkeleton from "../../components/GlobalSkeleton";
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UserAddress from "./AccountUserAddress";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import InfoIcon from '@mui/icons-material/Info';
import SupportIcon from '@mui/icons-material/Support';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useNavigate } from "react-router-dom";
import API from "../../api/api";
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import WcIcon from '@mui/icons-material/Wc';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  getCachedData,
  setCachedData
} from "../../utils/cache";


const editInputFormStyles = {
  mt: 2,
  mb: 0.5,
  '& .MuiInputLabel-root': {
    color: '#A59D96',
    fontSize: '0.85rem',
    fontWeight: '600',
    transform: 'translate(14px, -6px) scale(1)',
    bgcolor: '#FFFFFF',
    px: 0.5,
    '&.Mui-focused, &.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(1)',
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#F8C332'
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#ECE5DA',
    },
    '&:hover fieldset': {
      borderColor: '#F29D17',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F8C332',
    },
    '&.Mui-disabled': {
      bgcolor: '#FFFDF8',
      '& fieldset': { borderColor: '#F2EEE8' }
    }
  },
  '& .MuiOutlinedInput-input': {
    fontWeight: '700',
    color: '#2F2A25',
    py: 1.8
  }
};

const iconStyles = { color: '#5C4E42', flexShrink: 0, minWidth: 40 };

export default function AccountPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({ name: "", email: "", dob: "", gender: "" });
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");

    if (!savedUser) {
      console.log("No user found in storage, redirecting...");
      navigate("/login");
      return;
    }

    const parsedUser = JSON.parse(savedUser);

    if (!parsedUser.id && !parsedUser.user_id) {
      navigate("/login");
      return;
    }

    setUser(parsedUser);
    setEditData({
      name: parsedUser.name || "",
      email: parsedUser.email || "",
      dob: parsedUser.dob || "",
      gender: parsedUser.gender || ""
    });

    fetchUserAddresses(parsedUser.id || parsedUser.user_id);
  }, [navigate]);

  const fetchUserAddresses = async (userId) => {
    try {

      const cacheKey = `user_addresses_${userId}`;

      const cachedAddresses =
        getCachedData(cacheKey);

      if (cachedAddresses) {
        setAddresses(cachedAddresses);
        setLoading(false);
      } else {
        setLoading(true);
      }


      const res = await API.get(`/user/${userId}/addresses`);

      if (Array.isArray(res.data)) {

        setAddresses(res.data);

        // SAVE CACHE
        setCachedData(
          cacheKey,
          res.data
        );

        const defaultAddr = res.data.find(
          addr => addr.is_default
        );

        if (defaultAddr) {
          localStorage.setItem(
            "defaultAddress",
            JSON.stringify(defaultAddr)
          );

          window.dispatchEvent(
            new Event("addressChanged")
          );
        }
      }

    } catch (err) {
      console.error("Failed to fetch addresses", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await API.delete(`/user/address/${addressId}`);
      fetchUserAddresses(user.id || user.user_id); // Refresh the list after deleting
    } catch (err) {
      console.error("Failed to delete address", err);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      await API.put(`/user/address/${addressId}/set-default`);
      // Refresh the address list to show the new default
      fetchUserAddresses(user.id || user.user_id);
    } catch (err) {
      console.error("Failed to set default address", err);
    }
  };

  const handleEditAddress = (addr) => {
    // Close the modal and send them to the location page with edit data
    setOpenAddressModal(false);
    navigate("/location", { state: { editAddress: addr } });
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("user_access_token");
    localStorage.removeItem("user_refresh_token");
    navigate("/login");
    window.location.reload();
  };

  const handleUpdateProfile = async () => {
    try {
      await API.post("/user/update-profile", {
        user_id: user.id,
        name: editData.name,
        email: editData.email,
        dob: editData.dob,
        gender: editData.gender
      });

      const updatedUser = {
        ...user,
        name: editData.name,
        email: editData.email,
        dob: editData.dob,
        gender: editData.gender
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      console.log("Profile updated");
      setOpenEdit(false);
    } catch (err) {
      console.error("Update failed", err);
    }
  };


  return (
    // 👇 Reduced pb from 10 to 2 here
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 2 }}>
      <Box sx={{ pt: 1, zIndex: 1100, background: "#F8D548", borderRadius: "0 0 24px 24px", borderBottom: '2px solid #c3a169c4' }}>

        {/* 1. MODERN FLOATING PROFILE HEADER SECTION */}
        <Container maxWidth="md" sx={{ px: 1 }}>

          <Paper elevation={0} sx={{
            bgcolor: 'transparent',
            borderRadius: '20px',
            p: 2,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>

              {/* 1. Brand-Colored Avatar */}
              <Avatar sx={{
                width: 48, height: 48,
                background: '#FFFFFF',
                border: '2px solid #F8C332',
                color: '#3A2C23',
                fontWeight: '900',
                fontSize: '1.4rem',
                boxShadow: '0 4px 10px rgba(58, 44, 35, 0.05)'
              }}>
                {user?.name ? user.name[0].toUpperCase() : <PersonIcon />}
              </Avatar>

              {/* 2. Profile Details Block */}
              <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0, pr: 1 }}>
                <Typography variant="h6" fontWeight="900" noWrap sx={{ color: '#3A2C23', mb: 0.2, lineHeight: 1.2, fontSize: '1.1rem' }}>
                  {user?.name || "Mealno User"}
                </Typography>

                <Box sx={{ overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: '#5C4E42',
                      fontWeight: '600',
                      lineHeight: 1.3
                    }}
                  >
                    {user?.phone ? `+91 ${user.phone}` : "No phone available"}
                  </Typography>

                  {user?.email && (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: '#6E655E',
                        display: 'block',
                        fontWeight: '600'
                      }}
                    >
                      {user.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Modern Edit Pill Button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenEdit(true)}
                sx={{
                  color: '#3A2C23',
                  borderColor: '#ECE5DA',
                  bgcolor: '#FFFFFF',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: '800',
                  px: 2.5,
                  py: 0.5,
                  minWidth: 'auto',
                  boxShadow: '0 2px 8px rgba(58, 44, 35, 0.04)',
                  '&:hover': {
                    borderColor: '#F8C332',
                    bgcolor: '#FFFDF8'
                  }
                }}
              >
                Edit
              </Button>
            </Stack>
          </Paper>

        </Container >
      </Box>

      {/* ==============================================
         ✅ MENU LAYOUT MATCHING SHARED IMAGE
         ============================================== */}
      {loading && addresses.length === 0 ? (
        <Box sx={{ mt: 3 }}>
          <GlobalSkeleton type="list" />
        </Box>
      ) : (

        <Box sx={{ px: 1.5 }}>
          {/* 2. SECTION: FOOD DELIVERY */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 1,
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              border: 'none',
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Stack direction="row" sx={{ p: 1, pb: 0, gap: 1, alignItems: 'center' }}>
              <Box sx={{ bgcolor: '#F8C332', width: 4, height: 18, borderRadius: '4px' }} />
              <Typography variant="subtitle2" fontWeight="800" color="#3A2C23">
                Food delivery
              </Typography>
            </Stack>

            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate("/orders")}
                  sx={{ px: 1, py: 1.5 }}
                >
                  <ListItemIcon sx={iconStyles}>
                    <ShoppingBagIcon />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="700">
                        Your orders
                      </Typography>
                    }
                  />

                  <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20 }} />
                </ListItemButton>
              </ListItem>

              <Divider variant="middle" sx={{ bgcolor: '#f5f5f5', ml: 6, mr: 1 }} />

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => setOpenAddressModal(true)}
                  sx={{ px: 1, py: 1.5 }}
                >
                  <ListItemIcon sx={iconStyles}>
                    <LocationOnIcon />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="700">
                        Address book
                      </Typography>
                    }
                  />

                  <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20 }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>

          {/* 3. SECTION: MORE */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 1,
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              border: 'none',
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <List disablePadding>
              {["Your feedback", "About", "Help & Support"].map((text, idx) => (
                <React.Fragment key={text}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ px: 1, py: 1.5 }}
                      onClick={() => {
                        if (idx === 0) navigate("/FeedbackOption");
                        if (idx === 1) navigate("/about");
                        if (idx === 2) navigate("/help-support");
                      }}
                    >
                      <ListItemIcon sx={iconStyles}>
                        {idx === 0 && <ChatIcon />}
                        {idx === 1 && <InfoIcon />}
                        {idx === 2 && <SupportIcon />}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="700">
                            {text}
                          </Typography>
                        }
                      />
                      <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20 }} />
                    </ListItemButton>
                  </ListItem>
                  {idx < 2 && (
                    <Divider variant="middle" sx={{ bgcolor: '#f5f5f5', ml: 6, mr: 1 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* 4. SECTION: LOGOUT */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 1,
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              border: 'none',
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => setOpenLogoutConfirm(true)}
                  sx={{ px: 1, py: 1.5 }}
                >
                  <ListItemIcon sx={iconStyles}>
                    <PowerSettingsNewIcon />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="700">
                        Log out
                      </Typography>
                    }
                  />

                  <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20 }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>

          {/* FOOTER */}
          {/* 👇 Reduced mb from 3 to 1 here */}
          <Box sx={{ textAlign: 'center', mt: 4, mb: 1, opacity: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Mealno Alpha Version 1.0.0.0
            </Typography>

            <Typography variant="caption">
              © 2026 Mealno · Made with in Kolkata, India.
            </Typography>
          </Box>
        </Box>

      )}

      {/* ==============================================
         ✅ EDIT PROFILE DRAWER (BOTTOM SHEET)
         ============================================== */}
      < Drawer
        anchor="bottom"
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            overflow: 'visible',
            bgcolor: '#FFFDF8',
            maxHeight: '75vh'
          }
        }}
      >
        {/* 1. FLOATING DARK CROSS BUTTON */}
        < IconButton
          onClick={() => setOpenEdit(false)}
          sx={{
            position: 'absolute',
            top: -60,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(58, 44, 35, 0.7)',
            color: '#FFFFFF',
            width: 44,
            height: 44,
            border: "1px solid rgba(255,255,255,0.2)",
            zIndex: 9999,
            '&:hover': { bgcolor: 'rgba(58, 44, 35, 0.9)' }
          }}
        >
          <CloseIcon />
        </IconButton >

        {/* 2. DRAG HANDLE & TITLE AREA */}
        < Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.5, pb: 2 }}>
          < Box sx={{ width: 40, height: 5, bgcolor: '#ECE5DA', borderRadius: 3, mb: 1 }} />
          <Typography variant="subtitle1" fontWeight="900" sx={{ letterSpacing: 0.5, color: '#3A2C23' }}>
            Your Profile
          </Typography>
        </Box >

        {/* 3. SCROLLABLE CONTENT AREA */}
        < Box sx={{ px: 3, pb: 4, overflowY: 'auto' }}>

          {/* Avatar Section */}
          < Stack alignItems="center" sx={{ position: 'relative', mb: 4, mt: 0.5 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                border: '4px solid #FFFFFF',
                boxShadow: '0 8px 24px rgba(58, 44, 35, 0.08)',
                bgcolor: '#F8C332',
                color: '#3A2C23',
                fontWeight: '900',
                fontSize: '2.2rem'
              }}
            >
              {editData.name
                ? editData.name
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
                : <PersonIcon fontSize="large" />
              }
            </Avatar>
            <IconButton
              size="small"
              sx={{
                position: 'absolute', bottom: -5, right: '36%',
                bgcolor: '#FFFFFF', boxShadow: '0 4px 12px rgba(58, 44, 35, 0.1)',
                border: '2px solid #FFFFFF',
                '&:hover': { bgcolor: '#FFF6DE' }
              }}
            >
              <EditIcon sx={{ color: '#F29D17', fontSize: 16 }} />
            </IconButton>
          </Stack >

          {/* Form Inputs */}
          < Stack spacing={2.5} >
            <FormControl fullWidth variant="outlined" sx={editInputFormStyles}>
              <InputLabel htmlFor="profile-name">Name</InputLabel>
              <OutlinedInput
                id="profile-name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined" sx={editInputFormStyles} disabled>
              <InputLabel htmlFor="profile-phone">Mobile</InputLabel>
              <OutlinedInput
                id="profile-phone"
                label="Mobile"
                value={`+91 ${user?.phone}`}
              />
            </FormControl>

            {/* Email Field (Updated to match Mobile style) */}
            <FormControl fullWidth variant="outlined" sx={editInputFormStyles} disabled>
              <InputLabel htmlFor="profile-email">Email</InputLabel>
              <OutlinedInput
                id="profile-email"
                label="Email"
                value={editData.email}
              />
            </FormControl>

            {/* Functional Date of Birth Field */}
            <FormControl fullWidth variant="outlined" sx={editInputFormStyles}>
              {/* Note: shrink={true} is required so the label doesn't overlap the native date placeholder */}
              <InputLabel htmlFor="profile-dob" shrink={true}>Date of birth</InputLabel>
              <OutlinedInput
                id="profile-dob"
                type="date"
                value={editData.dob}
                onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
              />
            </FormControl>

            {/* Functional & Styled Gender Dropdown with Outlines/Dividers */}
            <FormControl fullWidth variant="outlined" sx={editInputFormStyles}>
              <InputLabel id="profile-gender-label">Gender</InputLabel>
              <Select
                labelId="profile-gender-label"
                id="profile-gender"
                value={editData.gender}
                onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                label="Gender"
                MenuProps={{
                  PaperProps: {
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      borderRadius: '16px',
                      border: '1px solid #d1d5db',
                      boxShadow: '0 10px 35px rgba(0, 0, 0, 0.1)',
                      '& .MuiList-root': { py: 1 },
                      '& .MuiMenuItem-root': {
                        mx: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: '10px',
                        fontWeight: '600',
                        color: '#555',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255, 75, 43, 0.06)',
                          color: '#FF4B2B',
                          transform: 'translateX(4px)'
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(45, 38, 37, 0.12)',
                          color: '#FF4B2B',
                          '&:hover': {
                            bgcolor: 'rgba(255, 75, 43, 0.18)',
                          }
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="Male">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <MaleIcon fontSize="small" sx={{ color: 'inherit' }} />
                    <span>Male</span>
                  </Stack>
                </MenuItem>

                {/* Outline separator */}
                <Divider sx={{ mx: 2, my: 0.5, borderColor: '#f0f0f0' }} />

                <MenuItem value="Female">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <FemaleIcon fontSize="small" sx={{ color: 'inherit' }} />
                    <span>Female</span>
                  </Stack>
                </MenuItem>

                {/* Outline separator */}
                <Divider sx={{ mx: 2, my: 0.5, borderColor: '#f0f0f0' }} />

                <MenuItem value="Other">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <WcIcon fontSize="small" sx={{ color: 'inherit' }} />
                    <span>Other</span>
                  </Stack>
                </MenuItem>

                {/* Outline separator */}
                <Divider sx={{ mx: 2, my: 0.5, borderColor: '#f0f0f0' }} />

                <MenuItem value="Prefer not to disclose">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <VisibilityOffIcon fontSize="small" sx={{ color: 'inherit' }} />
                    <span>Prefer not to disclose</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              disableElevation
              onClick={handleUpdateProfile}
              sx={{
                mt: 3,
                py: 1.8,
                borderRadius: '16px',
                bgcolor: '#F8C332',
                color: '#3A2C23',
                fontWeight: '900',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: '#F5B71D' }
              }}
            >
              Update profile
            </Button>
          </Stack >
        </Box >
      </Drawer >
      {/* ==============================================
         ✅ LOGOUT CONFIRMATION DIALOG
         ============================================== */}
      < Dialog
        open={openLogoutConfirm}
        onClose={() => setOpenLogoutConfirm(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 1,
            minWidth: '300px',
            textAlign: 'center',
            bgcolor: '#FFFDF8'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: '900', color: '#3A2C23', pb: 1 }}>
          Log Out?
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="#6E655E">
            Are you sure you want to log out of your account?
          </Typography>
        </DialogContent>

        <Stack direction="row" spacing={2} sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenLogoutConfirm(false)}
            sx={{
              borderRadius: '16px',
              color: '#6E655E',
              borderColor: '#858482',
              fontWeight: '800',
              textTransform: 'none',
              '&:hover': { borderColor: '#A59D96', bgcolor: '#FFF6DE' }
            }}
          >
            Stay here
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleLogout}
            sx={{
              borderRadius: '16px',
              bgcolor: '#6D4C41',
              color: '#FFFFFF',
              fontWeight: '800',
              textTransform: 'none',
              '&:hover': { bgcolor: '#D24F33' }
            }}
          >
            Log out
          </Button>
        </Stack>
      </Dialog >
      {/* ==============================================
         ✅ ADDRESS BOOK POP-UP COMPONENT
         ============================================== */}
      < UserAddress
        open={openAddressModal}
        onClose={() => setOpenAddressModal(false)}
        addresses={addresses}
        onDelete={handleDeleteAddress}
        onEdit={handleEditAddress}
        onSetDefault={handleSetDefaultAddress}
        isLoading={loading}
      />

    </Box >
  );
}